import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Code, Globe, Facebook, Instagram, MessageCircle, ExternalLink, Share2, Send, MessageSquareQuote, Loader2, CheckCircle2, Zap } from "lucide-react";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/apiClient";

const CopyRow = ({ value, testid, message = "Copied to clipboard" }) => {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(message); };
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 text-xs bg-[#0f0f11] border border-white/10 rounded-sm px-3 py-2.5 overflow-x-auto whitespace-pre text-muted-foreground">{value}</code>
      <Button data-testid={testid} onClick={copy} size="icon" variant="outline" className="rounded-sm border-white/10 shrink-0"><Copy className="h-4 w-4" /></Button>
    </div>
  );
};

export default function Connect() {
  const { company } = useOutletContext();
  const [meta, setMeta] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const loadMeta = () => api.get("/meta/config").then((r) => setMeta(r.data)).catch(() => setMeta({ enabled: false, connected_pages: [] }));

  useEffect(() => {
    loadMeta();
    const params = new URLSearchParams(window.location.search);
    const fb = params.get("fb");
    if (fb === "connected") {
      toast.success(`Facebook connected${params.get("pages") ? ` · ${params.get("pages")} page(s)` : ""}`);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (fb === "error") {
      toast.error("Couldn't connect Facebook. Please try again.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/meta/connect/start");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Facebook connection.");
      setConnecting(false);
    }
  };

  const disconnect = async (pageId) => {
    try {
      await api.post("/meta/disconnect", { page_id: pageId });
      toast.success("Page disconnected");
      loadMeta();
    } catch {
      toast.error("Couldn't disconnect. Please try again.");
    }
  };

  const publicUrl = `${window.location.origin}/c/${company?.slug}`;
  const shareText = `Get in touch with ${company?.name} — our AI receptionist Ava is online 24/7 to take your details and book you in:`;

  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Receptionist link copied"); };

  const greetingTemplate = `Thanks for messaging ${company?.name || "us"}! To help us deal with your enquiry as quickly as possible, please tap the link below to have a quick chat with our assistant. It only takes a moment to share a few details about what you need, and we'll get straight back to you.\n\n${publicUrl}`;
  const copyGreeting = () => { navigator.clipboard.writeText(greetingTemplate); toast.success("Greeting message copied"); };

  const iframeSnippet = `<iframe src="${publicUrl}" title="Book with ${company?.name}" style="width:100%;height:640px;border:0;border-radius:10px"></iframe>`;
  const buttonSnippet = `<a href="${publicUrl}" target="_blank" rel="noopener" style="position:fixed;bottom:24px;right:24px;background:#f97316;color:#fff;padding:14px 22px;border-radius:999px;font-family:system-ui,sans-serif;font-weight:600;text-decoration:none;box-shadow:0 6px 20px rgba(0,0,0,.3);z-index:99999">Book a job</a>`;

  const whatsappShare = `https://wa.me/?text=${encodeURIComponent(shareText + " " + publicUrl)}`;
  const facebookShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`;

  const savedLinks = [
    { name: "Website", icon: Globe, url: company?.website_url },
    { name: "Facebook", icon: Facebook, url: company?.facebook_url },
    { name: "Instagram", icon: Instagram, url: company?.instagram_url },
    { name: "LinkedIn", icon: Share2, url: company?.linkedin_url },
  ].filter((l) => l.url);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="connect-page">
      <div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">CONNECT AVA</h1>
        <p className="text-muted-foreground mt-1">Add your AI receptionist to WhatsApp, Instagram, Facebook and your website — one tap sends customers straight to Ava.</p>
      </div>

      {/* Receptionist link */}
      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6">
        <h3 className="font-display text-xl tracking-wide mb-1">YOUR RECEPTIONIST LINK</h3>
        <p className="text-sm text-muted-foreground mb-3">This is the link every button below shares. Send it to a customer and Ava takes it from there.</p>
        <CopyRow value={publicUrl} testid="copy-public-link" />
        <Button onClick={() => window.open(publicUrl, "_blank")} variant="ghost" className="rounded-sm text-primary mt-3 gap-2 px-0 hover:bg-transparent hover:text-orange-400"><ExternalLink className="h-4 w-4" /> Preview it</Button>
      </div>

      {/* Native Facebook Messenger (only when the integration is switched on) */}
      {meta?.enabled && (
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6" data-testid="messenger-native-card">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "rgba(24,119,242,.15)" }}>
              <Facebook className="h-5 w-5" style={{ color: "#1877f2" }} />
            </div>
            <div>
              <h3 className="font-display text-xl tracking-wide">FACEBOOK MESSENGER <span className="text-[10px] align-middle bg-primary/20 text-primary px-1.5 py-0.5 rounded-sm ml-1">NATIVE</span></h3>
              <p className="text-xs text-muted-foreground">Ava replies right inside your Facebook DMs — no link, no away message.</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground my-3">Connect your Facebook Page and Ava will answer Messenger enquiries automatically, qualify the lead and book a callback — exactly like she does on your receptionist link.</p>

          {meta.connected_pages?.length > 0 ? (
            <div className="space-y-2">
              {meta.connected_pages.map((pg) => (
                <div key={pg.page_id} className="flex items-center justify-between gap-3 bg-[#0f0f11] border border-white/10 rounded-sm px-4 py-3" data-testid={`connected-page-${pg.page_id}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    <span className="text-sm truncate">{pg.page_name || pg.page_id}</span>
                    <span className="text-[10px] text-green-500 border border-green-500/30 rounded-sm px-1.5 py-0.5 shrink-0">Ava is live</span>
                  </div>
                  <Button data-testid={`disconnect-page-${pg.page_id}`} onClick={() => disconnect(pg.page_id)} size="sm" variant="outline" className="rounded-sm border-white/10 shrink-0">Disconnect</Button>
                </div>
              ))}
              <Button data-testid="fb-connect-another-btn" onClick={startConnect} disabled={connecting} variant="ghost" className="rounded-sm text-primary gap-2 px-0 hover:bg-transparent hover:text-orange-400">
                {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Facebook className="h-4 w-4" />} Connect another page
              </Button>
            </div>
          ) : (
            <Button data-testid="fb-connect-btn" onClick={startConnect} disabled={connecting} className="rounded-sm text-white gap-2 justify-center" style={{ background: "#1877f2" }}>
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Connect Facebook Page
            </Button>
          )}
        </div>
      )}

      {/* Auto-reply greeting */}
      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6" data-testid="greeting-card">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-sm bg-primary/15 flex items-center justify-center shrink-0"><MessageSquareQuote className="h-5 w-5 text-primary" /></div>
          <h3 className="font-display text-xl tracking-wide">SET AN AUTO-REPLY GREETING</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Here's the clever bit: copy your link above and add it to an <span className="text-foreground">automatic greeting / away message</span> on your <span className="text-foreground">WhatsApp Business</span>, <span className="text-foreground">Facebook</span> and <span className="text-foreground">Instagram</span>. That way, the moment a customer messages you, they get an instant reply pointing them to Ava — who gathers all the details for you while you're on the tools. Here's a message you can use (feel free to tweak it):
        </p>
        <div className="flex items-start gap-2">
          <div className="flex-1 text-sm bg-[#0f0f11] border border-white/10 rounded-sm px-4 py-3 whitespace-pre-wrap text-muted-foreground leading-relaxed" data-testid="greeting-text">{greetingTemplate}</div>
          <Button data-testid="copy-greeting-btn" onClick={copyGreeting} size="icon" variant="outline" className="rounded-sm border-white/10 shrink-0"><Copy className="h-4 w-4" /></Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Tip: in WhatsApp Business, set this under <span className="text-foreground">Settings → Business tools → Greeting message</span> (and/or Away message). On Facebook & Instagram you'll find it under <span className="text-foreground">Inbox → Automations / Instant reply</span>.</p>
      </div>

      {/* Platform cards */}
      <div className="grid sm:grid-cols-2 gap-5">
        {/* WhatsApp */}
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 flex flex-col" data-testid="platform-whatsapp">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "rgba(37,211,102,.15)" }}>
              <MessageCircle className="h-5 w-5" style={{ color: "#25d366" }} />
            </div>
            <h3 className="font-display text-xl tracking-wide">WHATSAPP</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4 flex-1">Send the link over WhatsApp, or paste it into your WhatsApp Business greeting/away message so every enquiry gets Ava instantly.</p>
          <div className="flex flex-col gap-2">
            <Button data-testid="whatsapp-share-btn" onClick={() => window.open(whatsappShare, "_blank")} className="rounded-sm text-white gap-2 justify-center" style={{ background: "#25d366" }}>
              <Send className="h-4 w-4" /> Share on WhatsApp
            </Button>
            <Button data-testid="whatsapp-copy-btn" onClick={copyLink} variant="outline" className="rounded-sm border-white/10 gap-2 justify-center"><Copy className="h-4 w-4" /> Copy link for auto-reply</Button>
          </div>
        </div>

        {/* Instagram */}
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 flex flex-col" data-testid="platform-instagram">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "rgba(225,48,108,.15)" }}>
              <Instagram className="h-5 w-5" style={{ color: "#e1306c" }} />
            </div>
            <h3 className="font-display text-xl tracking-wide">INSTAGRAM</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4 flex-1">Add the link to your Instagram bio and drop it into Stories with a link sticker so followers can book in a tap.</p>
          <div className="flex flex-col gap-2">
            <Button data-testid="instagram-copy-btn" onClick={copyLink} className="rounded-sm text-white gap-2 justify-center" style={{ background: "#e1306c" }}><Copy className="h-4 w-4" /> Copy link for bio</Button>
            <a href="https://www.instagram.com/accounts/edit/" target="_blank" rel="noopener noreferrer" data-testid="instagram-edit-link" className="text-xs text-center text-muted-foreground hover:text-primary transition-colors">Open Instagram profile settings →</a>
          </div>
        </div>

        {/* Facebook */}
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 flex flex-col" data-testid="platform-facebook">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "rgba(24,119,242,.15)" }}>
              <Facebook className="h-5 w-5" style={{ color: "#1877f2" }} />
            </div>
            <h3 className="font-display text-xl tracking-wide">FACEBOOK</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4 flex-1">Share a post with your link, or set it as the "Book Now" button on your Facebook business page.</p>
          <div className="flex flex-col gap-2">
            <Button data-testid="facebook-share-btn" onClick={() => window.open(facebookShare, "_blank")} className="rounded-sm text-white gap-2 justify-center" style={{ background: "#1877f2" }}><Send className="h-4 w-4" /> Share to Facebook</Button>
            <Button data-testid="facebook-copy-btn" onClick={copyLink} variant="outline" className="rounded-sm border-white/10 gap-2 justify-center"><Copy className="h-4 w-4" /> Copy link for page button</Button>
          </div>
        </div>

        {/* Website */}
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 flex flex-col" data-testid="platform-website">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "rgba(249,115,22,.15)" }}>
              <Code className="h-5 w-5 text-primary" />
            </div>
            <h3 className="font-display text-xl tracking-wide">WEBSITE</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4 flex-1">Embed Ava directly on your site or add a floating "Book a job" button. Paste into any custom-code / embed block.</p>
          {company?.is_pro === false ? (
            <div className="bg-primary/10 border border-primary/30 rounded-sm p-4" data-testid="embed-pro-lock">
              <p className="text-sm">The website embed widget is a <span className="text-primary font-medium">Pro</span> feature. On Lite you can still share your receptionist link anywhere. Upgrade on the Billing tab to embed Ava on your site.</p>
            </div>
          ) : (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Full chat embed (iframe):</p>
              <CopyRow value={iframeSnippet} testid="copy-iframe" message="Embed code copied" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Floating "Book a job" button:</p>
              <CopyRow value={buttonSnippet} testid="copy-button" message="Button code copied" />
            </div>
          </div>
          )}
        </div>
      </div>

      {savedLinks.length > 0 && (
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6">
          <h3 className="font-display text-xl tracking-wide mb-4">YOUR SAVED LINKS</h3>
          <div className="flex flex-wrap gap-3">
            {savedLinks.map((l) => (
              <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary border border-white/10 rounded-sm px-3 py-2 hover:bg-secondary transition-[background-color] duration-200">
                <l.icon className="h-4 w-4" /> {l.name}
              </a>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Manage these in Settings.</p>
        </div>
      )}
    </div>
  );
}
